import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { getToken, onMessage } from 'firebase/messaging';
import toast from 'react-hot-toast';

import { messaging } from './firebaseConfig';
import { setToken, setUnreadCount } from 'src/store/slices/userSlice';
import { CustomToast } from 'src/pages/components/customToast';
import { useMockedUser } from 'src/hooks/use-mocked-user';
import { USER_TYPE_SHOPADMIN } from 'src/config-global';

const FLUSH_DELAY_MS = 400;

const TOAST_LEADER_LOCK = 'pw-admin-notification-toast-leader';

const NotificationHandler = () => {
  const dispatch = useDispatch();
  const unsubscribeRef = useRef(null);
  const seenIdsRef = useRef(new Set());       // dedup across the two listeners
  const queueRef = useRef([]);                // foreground burst queue
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);         // guards the onMessage race on fast unmount
  const isLeaderRef = useRef(false);          // only the leader tab shows a toast
  const releaseLeaderRef = useRef(null);

  const { user } = useMockedUser();
  const isShopAdmin = user?.user_type === USER_TYPE_SHOPADMIN;
  const REACT_APP_VAPID_KEY = process.env.REACT_APP_VAPID_KEY;

  // ---- shared: process a batch of payloads with ONE dispatch + safe toasts ----
  const processBatch = (payloads, { countTowardUnread = true } = {}) => {
    const fresh = payloads.filter((p) => {
      const id = p?.messageId || p?.collapseKey || p?.data?.id;
      if (!id) return true; // no id to dedup on, allow it through
      if (seenIdsRef.current.has(id)) return false;
      seenIdsRef.current.add(id);
      return true;
    });

    if (fresh.length === 0) return;

    if (countTowardUnread) {
      dispatch(setUnreadCount(fresh.length)); // one dispatch for the whole burst
    }

    // Every tab dedupes and updates its own unread count above, but only the
    // elected leader tab actually displays a toast — otherwise each open tab
    // pops its own copy of the same notification.
    if (!isLeaderRef.current) return;

    if (fresh.length === 1) {
      toast.custom((t) => <CustomToast payload={fresh[0]} t={t} />);
    } else {
      // collapse into a single summary toast instead of N toasts
      const summaryPayload = {
        notification: {
          title: 'New notifications',
          body: `You have ${fresh.length} new updates`,
        },
      };
      toast.custom((t) => <CustomToast payload={summaryPayload} t={t} />);
    }
  };

  const requestPermission = async () => {
    try {
      if (!('Notification' in window)) return;
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      if (!('serviceWorker' in navigator)) return;

      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      await navigator.serviceWorker.ready;
      if (!messaging) return;

      let token = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          token = await getToken(messaging, {
            vapidKey: REACT_APP_VAPID_KEY,
            serviceWorkerRegistration: registration,
          });
          if (token) break;
        } catch (err) {
          console.warn(`Token fetch attempt ${attempt} failed:`, err?.message || err);
          if (attempt < 3) await new Promise((res) => setTimeout(res, 2000));
        }
      }
      if (token) dispatch(setToken(token));

      if (typeof unsubscribeRef.current === 'function') unsubscribeRef.current();

      // foreground messages: also batched, in case of a burst while tab is focused
      const unsubscribe = onMessage(messaging, (payload) => {
        console.log('[FCM] Foreground notification', payload);
        if (!payload) return;
        queueRef.current.push(payload);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          const batch = queueRef.current;
          queueRef.current = [];
          processBatch(batch, { countTowardUnread: true });
        }, FLUSH_DELAY_MS);
      });

      // The permission/token flow above is async and can outlive the component
      // (fast route change). If we unmounted while awaiting, the effect cleanup
      // already ran and found unsubscribeRef.current still null — it unsubscribed
      // nothing. Detect that here and unsubscribe immediately instead of leaking
      // a listener that lives on the shared `messaging` singleton forever.
      if (cancelledRef.current) {
        unsubscribe();
        return;
      }
      unsubscribeRef.current = unsubscribe;
    } catch (error) {
      console.error('Notification permission error:', error);
    }
  };

  useEffect(() => {
    cancelledRef.current = false;
    requestPermission();
    return () => {
      cancelledRef.current = true;
      if (typeof unsubscribeRef.current === 'function') unsubscribeRef.current();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Elect one leader tab (via the Web Locks API) to own toast display.
  // Chosen over BroadcastChannel because the browser already guarantees
  // mutual exclusion and re-election on tab close/crash for free — no
  // custom heartbeat/election protocol to get wrong.
  useEffect(() => {
    if (!('locks' in navigator)) {
      // No Web Locks support: fall back to every tab showing its own toast
      // rather than silently showing none.
      isLeaderRef.current = true;
      return undefined;
    }

    let active = true;
    navigator.locks.request(TOAST_LEADER_LOCK, { mode: 'exclusive' }, () => {
      if (!active) return undefined;
      isLeaderRef.current = true;
      return new Promise((resolve) => {
        releaseLeaderRef.current = resolve;
      });
    });

    return () => {
      active = false;
      isLeaderRef.current = false;
      if (releaseLeaderRef.current) {
        releaseLeaderRef.current();
        releaseLeaderRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker) return undefined;

    const handleServiceWorkerMessage = (event) => {
      console.log('[SW] Background notification', event.data);
      if (event.data?.type === 'BACKGROUND_NOTIFICATION') {
        const payloads = event.data.payloads || [event.data.payload]; // backward compatible
        console.log('[Batch] Processing', payloads);
        processBatch(payloads, { countTowardUnread: !isShopAdmin });
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [isShopAdmin]);

  return null;
};

export default NotificationHandler;